import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, Sparkles, Wand2, Save, Eye, EyeOff,
  Check, RefreshCw, Plus, Trash2, ChevronUp, ChevronDown, GripVertical,
  ChevronRight, Upload, X, History, RotateCcw,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import RichTextEditor from '../../components/RichTextEditor';
import { useAdminTheme, tc } from './theme';
import AiImageButton from './AiImageButton';
import PageVersions from './PageVersions';

// ---------------------------------------------------------------------------
// AI action chip — Generate / Improve / Shorten / Expand on any field
// ---------------------------------------------------------------------------
function AiActions({ field, page, section, onApply, theme }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  // Heuristic: pick a sensible prompt based on the field's key/type
  const pickPromptKey = () => {
    if (field.key === 'title' || field.key === 'title_highlight' || field.key === 'subtitle') return 'hero_copy';
    if (field.key === 'description' || field.key === 'short') return 'service_description';
    if (field.key === 'features') return 'service_features';
    if (field.key === 'a' && field.label?.toLowerCase().includes('answer')) return 'faq_answer';
    return 'improve_text';
  };

  const run = async (action) => {
    setBusy(action);
    setErr(null);
    try {
      const res = await api.post('/api/admin-v2/ai/generate', {
        prompt_key: action === 'generate' ? pickPromptKey() : 'improve_text',
        action,
        current_text: typeof field.value === 'string' ? field.value : '',
        variables: {
          page_name: page?.name || '',
          service_name: page?.name || '',
          section_title: section?.title || '',
          target_length: action === 'shorten' ? '40' : action === 'expand' ? '200' : '120',
          primary_keyword: page?.name || '',
          question: field.label || field.key,
        },
      });

      const data = res.data || {};
      // If prompt declared JSON and parsed object has a key matching this field, use that.
      if (data.parsed && typeof data.parsed === 'object' && data.parsed[field.key] != null) {
        onApply(data.parsed[field.key]);
      } else {
        onApply(data.output || '');
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const btnClass = theme === 'light'
    ? 'bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100'
    : 'bg-violet-500/15 text-violet-300 border border-violet-500/30 hover:bg-violet-500/25 hover:text-white';

  const Btn = ({ action, label, icon: Icon }) => (
    <button
      type="button"
      disabled={busy != null}
      onClick={() => run(action)}
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md disabled:opacity-50 transition-all ${btnClass}`}
      data-testid={`ai-${action}-${field.key}`}
    >
      {busy === action ? <Loader2 size={11} className="animate-spin" /> : <Icon size={11} />}
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      <Btn action="generate" label="Generate" icon={Sparkles} />
      <Btn action="improve" label="Improve" icon={Wand2} />
      <Btn action="shorten" label="Shorten" icon={RefreshCw} />
      <Btn action="expand" label="Expand" icon={RefreshCw} />
      {err && (
        <span className={`text-[11px] flex items-center gap-1 ${theme === 'light' ? 'text-rose-700' : 'text-rose-300'}`}>
          <AlertCircle size={11} /> {err}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Video field — URL input + Upload button (multipart to /api/admin/upload)
// + inline preview player. Same shape as ImageField, just accepts mp4/webm
// MIME types and renders a <video> instead of an <img>.
// ---------------------------------------------------------------------------
function VideoField({ id, value, onChange, theme }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append('file', files[0]);
      form.append('folder', 'videos');
      const res = await api.post('/api/admin/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.url;
      if (url) onChange(url);
      else throw new Error('Upload returned no URL');
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={id}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`flex-1 min-w-[200px] px-3 py-2 rounded-lg outline-none text-sm font-mono ${tc.inputBg(theme)}`}
          placeholder="/assets/videos/example.mp4 or https://…"
        />
        <input
          ref={fileRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime,video/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap transition-all disabled:opacity-50 ${
            theme === 'light'
              ? 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
              : 'bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10 hover:text-white'
          }`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {busy ? 'Uploading…' : 'Upload video'}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className={`p-2 rounded-lg transition-all ${
              theme === 'light' ? 'text-rose-600 hover:bg-rose-50' : 'text-rose-300 hover:bg-rose-500/10'
            }`}
            title="Clear video"
          >
            <X size={12} />
          </button>
        )}
      </div>
      {err && (
        <p className={`text-[11px] flex items-center gap-1 ${theme === 'light' ? 'text-rose-700' : 'text-rose-300'}`}>
          <AlertCircle size={11} /> {err}
        </p>
      )}
      {value && (
        <div className={`rounded-lg overflow-hidden border ${tc.divider(theme)} max-w-md`}>
          <video
            src={value}
            controls
            muted
            playsInline
            preload="metadata"
            className="w-full h-auto"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      <p className={`text-[11px] ${tc.textDim(theme)}`}>
        Tip: MP4 (H.264) plays everywhere. Keep file size under 5 MB for fast page loads — use
        Handbrake or ffmpeg to compress longer clips.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Build a contextual default prompt for the AI image generator based on the
// page name, section type/title and the field's key/label. This is just a
// starting suggestion — the admin can edit the prompt freely in the modal.
// ---------------------------------------------------------------------------
function buildImageAiHint(field, section, page) {
  const pageName = page?.name || page?.title || '';
  const sectionLabel = section?.title || section?.label || '';
  const fieldKey = (field?.key || field?.label || 'image').toLowerCase();

  if (fieldKey.includes('og') || fieldKey.includes('twitter') || fieldKey.includes('share')) {
    return pageName
      ? `Social-share preview image for "${pageName}". Wide 1.91:1 layout, marketing-quality, professional, no text overlay.`
      : 'Wide social-share preview image, marketing-quality, professional, no text overlay.';
  }
  if (fieldKey.includes('hero') || sectionLabel.toLowerCase().includes('hero')) {
    return pageName
      ? `Hero illustration for the "${pageName}" page. Modern, business / telecoms theme, violet & fuchsia accents, no text.`
      : 'Hero illustration, business / telecoms theme, violet & fuchsia accents, no text.';
  }
  if (fieldKey.includes('background') || fieldKey.includes('banner')) {
    return `Wide background banner for the "${pageName || 'page'}", clean, subtle pattern, no text.`;
  }
  if (fieldKey.includes('icon') || fieldKey.includes('logo')) {
    return `Minimalist icon for ${sectionLabel || fieldKey}, flat vector, single subject, transparent feel, violet accent.`;
  }
  return pageName
    ? `Illustration for the "${pageName}" page${sectionLabel ? ` (${sectionLabel} section)` : ''}. Marketing-quality, professional, no text.`
    : 'Marketing illustration, professional, no text.';
}

// ---------------------------------------------------------------------------
// Image field — URL input + Upload button (multipart to /api/admin/upload)
// + live preview thumbnail. The backend returns `{ filename, url }`; we
// store the relative url path in the field value so the same image works
// on both production and dev.
// ---------------------------------------------------------------------------
function ImageField({ id, value, onChange, theme, aiHint }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append('file', files[0]);
      form.append('folder', 'pages');
      const res = await api.post('/api/admin/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.url;
      if (url) onChange(url);
      else throw new Error('Upload returned no URL');
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          id={id}
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`flex-1 min-w-[200px] px-3 py-2 rounded-lg outline-none text-sm font-mono ${tc.inputBg(theme)}`}
          placeholder="/assets/example.webp or https://..."
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap transition-all disabled:opacity-50 ${
            theme === 'light'
              ? 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200'
              : 'bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10 hover:text-white'
          }`}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {busy ? 'Uploading…' : 'Upload'}
        </button>
        <AiImageButton
          defaultPrompt={aiHint || ''}
          folder="pages"
          onGenerated={(url) => onChange(url)}
          size="sm"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className={`p-2 rounded-lg transition-all ${
              theme === 'light' ? 'text-rose-600 hover:bg-rose-50' : 'text-rose-300 hover:bg-rose-500/10'
            }`}
            title="Clear image"
          >
            <X size={12} />
          </button>
        )}
      </div>
      {err && (
        <p className={`text-[11px] flex items-center gap-1 ${theme === 'light' ? 'text-rose-700' : 'text-rose-300'}`}>
          <AlertCircle size={11} /> {err}
        </p>
      )}
      {value && (
        <div className={`rounded-lg overflow-hidden border ${tc.divider(theme)} max-w-xs`}>
          <img
            src={value}
            alt="preview"
            className="w-full h-auto object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plain sub-field row used inside a list-typed field. Lighter than the full
// FieldRow (no surrounding card, no char counter, no AI chip header — but
// AI buttons still attach so admins can generate a service's description
// or an FAQ answer per item).
// ---------------------------------------------------------------------------
function SubFieldRow({ schemaField, value, onChange, page, section, theme }) {
  const inputCls = `w-full px-3 py-2 rounded-lg outline-none text-sm transition-all ${tc.inputBg(theme)}`;
  // Reuse AiActions by faking a `field` shape; the prompt picker reads .key
  const pseudoField = { ...schemaField, value };

  return (
    <div>
      <label className={`block text-[10px] uppercase tracking-widest font-bold mb-1 ${tc.textMuted(theme)}`}>
        {schemaField.label || schemaField.key}
        {schemaField.help && (
          <span className={`ml-2 normal-case font-normal text-[10px] ${tc.textDim(theme)}`}>{schemaField.help}</span>
        )}
      </label>
      {schemaField.type === 'textarea' || schemaField.type === 'richtext' ? (
        <textarea
          rows={3}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} resize-y`}
          placeholder={schemaField.label?.toLowerCase() || ''}
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          placeholder={schemaField.label?.toLowerCase() || ''}
        />
      )}
      <AiActions field={pseudoField} page={page} section={section} onApply={onChange} theme={theme} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// List-typed field — `value` is an array of objects shaped by `item_schema`.
// Users can add, remove, reorder items; each item's fields edit inline.
// ---------------------------------------------------------------------------
function ListField({ field, page, section, onChange, theme }) {
  const items = Array.isArray(field.value) ? field.value : [];
  const schema = field.item_schema || [];
  const itemLabel = field.item_label || 'Item';

  const updateItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  const addItem = () => {
    const blank = Object.fromEntries(schema.map((s) => [s.key, '']));
    onChange([...items, blank]);
  };
  const moveItem = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div className={`rounded-xl p-3 ${tc.cardMutedBg(theme)}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className={`text-[10px] uppercase tracking-widest font-bold ${tc.textMuted(theme)}`}>
            {field.label}
            <span className={`ml-2 normal-case font-normal ${tc.textDim(theme)}`}>
              ({items.length} {itemLabel.toLowerCase()}{items.length === 1 ? '' : 's'})
            </span>
          </p>
          {field.help && (
            <p className={`text-[11px] mt-0.5 ${tc.textDim(theme)}`}>{field.help}</p>
          )}
        </div>
        <button
          type="button"
          onClick={addItem}
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md transition-all ${
            theme === 'light'
              ? 'bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100'
              : 'bg-violet-500/20 text-violet-200 border border-violet-500/40 hover:bg-violet-500/30'
          }`}
        >
          <Plus size={12} /> Add {itemLabel.toLowerCase()}
        </button>
      </div>

      {items.length === 0 && (
        <p className={`text-xs italic ${tc.textDim(theme)}`}>
          No items yet — click <strong>Add {itemLabel.toLowerCase()}</strong> to create the first one.
        </p>
      )}

      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className={`rounded-lg p-3 space-y-2 ${tc.cardBg(theme)}`}>
            <div className="flex items-center justify-between">
              <p className={`text-[10px] uppercase tracking-widest font-bold ${tc.textMuted(theme)}`}>
                <GripVertical size={11} className="inline -mt-0.5 mr-1" />
                {itemLabel} #{idx + 1}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => moveItem(idx, -1)}
                  disabled={idx === 0}
                  className={`p-1 rounded ${tc.textMuted(theme)} ${tc.hoverBg(theme)} disabled:opacity-30`}
                  title="Move up"
                >
                  <ChevronUp size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => moveItem(idx, 1)}
                  disabled={idx === items.length - 1}
                  className={`p-1 rounded ${tc.textMuted(theme)} ${tc.hoverBg(theme)} disabled:opacity-30`}
                  title="Move down"
                >
                  <ChevronDown size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className={`p-1 rounded transition-all ${
                    theme === 'light' ? 'text-rose-600 hover:bg-rose-50' : 'text-rose-300 hover:bg-rose-500/10'
                  }`}
                  title={`Remove ${itemLabel.toLowerCase()}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {schema.map((s) => (
                <div key={s.key} className={s.type === 'textarea' || s.type === 'richtext' ? 'sm:col-span-2' : ''}>
                  <SubFieldRow
                    schemaField={s}
                    value={item[s.key]}
                    onChange={(v) => updateItem(idx, { [s.key]: v })}
                    page={page}
                    section={section}
                    theme={theme}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field input — renders a different widget per field type
// ---------------------------------------------------------------------------
function FieldRow({ field, page, section, onChange, theme }) {
  // List-typed field has its own renderer; bypass the flat-field layout
  if (field.type === 'list') {
    return <ListField field={field} page={page} section={section} onChange={onChange} theme={theme} />;
  }

  const id = `field-${section.id}-${field.key}`;
  const charLimit = field.type === 'text' ? 90 : field.type === 'textarea' ? 280 : null;
  const charCount = typeof field.value === 'string' ? field.value.length : 0;
  const inputCls = `w-full px-3 py-2 rounded-lg outline-none text-sm transition-all ${tc.inputBg(theme)}`;

  return (
    <div className={`rounded-xl p-3 ${tc.cardMutedBg(theme)}`}>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <label htmlFor={id} className={`text-[10px] uppercase tracking-widest font-bold ${tc.textMuted(theme)}`}>
          {field.label}
          {field.help && <span className={`ml-2 normal-case font-normal text-[10px] ${tc.textDim(theme)}`}>{field.help}</span>}
        </label>
        {charLimit && (
          <span className={`text-[10px] ${charCount > charLimit ? (theme === 'light' ? 'text-rose-700' : 'text-rose-300') : tc.textDim(theme)}`}>
            {charCount}/{charLimit}
          </span>
        )}
      </div>

      {field.type === 'richtext' ? (
        <div className={theme === 'light' ? 'rich-editor-light' : ''}>
          <RichTextEditor
            value={field.value || ''}
            onChange={(v) => onChange(v)}
            placeholder={`Write ${field.label?.toLowerCase() || 'content'}…`}
          />
        </div>
      ) : field.type === 'textarea' ? (
        <textarea
          id={id}
          rows={4}
          value={field.value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputCls} resize-y`}
          placeholder={`Write ${field.label?.toLowerCase() || 'value'}…`}
        />
      ) : field.type === 'image' ? (
        <ImageField
          id={id}
          value={field.value}
          onChange={onChange}
          theme={theme}
          aiHint={buildImageAiHint(field, section, page)}
        />
      ) : field.type === 'video' ? (
        <VideoField
          id={id}
          value={field.value}
          onChange={onChange}
          theme={theme}
        />
      ) : (
        <input
          id={id}
          type="text"
          value={field.value || ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          placeholder={`Write ${field.label?.toLowerCase() || 'value'}…`}
        />
      )}

      <AiActions
        field={field}
        page={page}
        section={section}
        onApply={(text) => onChange(text)}
        theme={theme}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section card — collapsible block with all its fields. Section header acts
// as the toggle (click anywhere on it). Eye toggle and chevron sit on the
// right for power use. `expanded` is owned by the parent so the section
// navigator can scroll/expand a chosen section from outside.
// ---------------------------------------------------------------------------
function SectionCard({ section, page, onSectionChange, theme, expanded, onExpandedChange, anchorId }) {
  const updateField = (key, value) => {
    onSectionChange({
      ...section,
      fields: section.fields.map((f) => (f.key === key ? { ...f, value } : f)),
    });
  };

  const toggleEnabled = (e) => {
    e.stopPropagation();
    onSectionChange({ ...section, enabled: !section.enabled });
  };

  return (
    <div id={anchorId} className={`rounded-2xl overflow-hidden scroll-mt-20 ${tc.cardBg(theme)}`}>
      <button
        type="button"
        onClick={() => onExpandedChange(!expanded)}
        className={`w-full flex items-center justify-between px-5 py-3 border-b text-left transition-colors ${tc.divider(theme)} ${tc.cardMutedBg(theme)} ${tc.hoverBg(theme)}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronRight
            size={16}
            className={`shrink-0 ${tc.textMuted(theme)} transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
          <div className="min-w-0">
            <p className={`font-bold truncate ${tc.textPrimary(theme)}`}>{section.title}</p>
            <p className={`text-[10px] uppercase tracking-widest font-mono ${tc.textDim(theme)}`}>
              type={section.type} · {section.fields?.length || 0} field{section.fields?.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <span
          onClick={toggleEnabled}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
            section.enabled
              ? (theme === 'light' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100' : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25')
              : (theme === 'light' ? 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200' : 'bg-slate-500/15 text-slate-400 border border-slate-500/30 hover:bg-slate-500/25')
          }`}
        >
          {section.enabled ? <Eye size={12} /> : <EyeOff size={12} />}
          {section.enabled ? 'Visible' : 'Hidden'}
        </span>
      </button>

      {expanded && (
        <div className="p-5 space-y-3">
          {(!section.fields || section.fields.length === 0) && (
            <p className={`text-sm ${tc.textDim(theme)}`}>No fields defined for this section yet.</p>
          )}
          {section.fields?.map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              page={page}
              section={section}
              onChange={(value) => updateField(field.key, value)}
              theme={theme}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main editor
// ---------------------------------------------------------------------------
export default function AdminV2PageEditor() {
  const { slug } = useParams();
  const { theme } = useAdminTheme();
  const [page, setPage] = useState(null);
  const [original, setOriginal] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Expanded section is keyed by section.id. Only one is open at a time —
  // keeps the screen tidy on pages with many sections.
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/admin-v2/pages/${slug}`);
      setPage(res.data);
      setOriginal(JSON.stringify(res.data));
      // Auto-open the first section so the page isn't a wall of titles
      const firstId = res.data?.sections?.[0]?.id;
      if (firstId) setOpenId(firstId);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const jumpToSection = (sectionId) => {
    setOpenId(sectionId);
    // Defer scroll so the expanded block has time to render
    setTimeout(() => {
      const el = document.getElementById(`section-${sectionId}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  };

  useEffect(() => {
    load();
  }, [load]);

  const isDirty = page && original && JSON.stringify(page) !== original;

  const handleSectionChange = (idx, updatedSection) => {
    setPage({
      ...page,
      sections: page.sections.map((s, i) => (i === idx ? updatedSection : s)),
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: page.name,
        public_path: page.public_path,
        sections: page.sections,
        enabled: page.enabled,
      };
      const res = await api.put(`/api/admin-v2/pages/${slug}`, body);
      setPage(res.data);
      setOriginal(JSON.stringify(res.data));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link
          to="/admin/pages"
          className={`inline-flex items-center gap-1 text-sm ${tc.textMuted(theme)} hover:${tc.textPrimary(theme)}`}
        >
          <ArrowLeft size={14} /> All pages
        </Link>
        {page && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowHistory(true)}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg ${theme === 'light' ? 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50' : 'bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10 hover:text-white'}`}
            >
              <History size={13} /> History
            </button>
            <Link
              to={page.public_path}
              target="_blank"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg ${theme === 'light' ? 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50' : 'bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10 hover:text-white'}`}
            >
              View live
            </Link>
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="page-save-btn"
            >
              {saving ? (
                <Loader2 size={14} className="animate-spin" />
              ) : saved ? (
                <Check size={14} />
              ) : (
                <Save size={14} />
              )}
              {saving ? 'Saving…' : saved ? 'Saved' : 'Save changes'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${theme === 'light' ? 'text-rose-700 bg-rose-50 border border-rose-200' : 'text-rose-300 bg-rose-500/10 border border-rose-500/30'}`}>
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !error && (
        <div className={`flex items-center gap-2 text-sm ${tc.textMuted(theme)}`}>
          <Loader2 size={16} className="animate-spin" /> Loading page…
        </div>
      )}

      {page && (
        <>
          <div>
            <p className={`text-xs uppercase tracking-widest font-bold ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300/80'}`}>Page</p>
            <h1 className={`text-3xl font-extrabold mt-1 ${tc.textPrimary(theme)}`}>{page.name}</h1>
            <p className={`text-xs font-mono mt-1 ${tc.textDim(theme)}`}>{page.public_path}</p>
          </div>

          {isDirty && (
            <div className={`rounded-xl px-4 py-2 text-xs ${theme === 'light' ? 'bg-amber-50 border border-amber-200 text-amber-700' : 'bg-amber-500/10 border border-amber-500/30 text-amber-300'}`}>
              You have unsaved changes.
            </div>
          )}

          <div className="grid lg:grid-cols-[220px_minmax(0,1fr)] gap-6">
            {/* Section navigator — sticky on desktop, hidden on mobile */}
            {page.sections && page.sections.length > 1 && (
              <aside className="hidden lg:block">
                <div className={`sticky top-20 rounded-2xl p-3 space-y-1 ${tc.cardBg(theme)}`}>
                  <p className={`px-2 py-1 text-[10px] uppercase tracking-widest font-bold ${tc.textDim(theme)}`}>
                    Sections
                  </p>
                  {page.sections.map((s) => {
                    const active = openId === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => jumpToSection(s.id)}
                        className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                          active
                            ? (theme === 'light' ? 'bg-violet-50 text-violet-700 font-semibold' : 'bg-violet-500/20 text-white font-semibold')
                            : `${tc.textMuted(theme)} ${tc.hoverBg(theme)}`
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          s.enabled
                            ? (theme === 'light' ? 'bg-emerald-500' : 'bg-emerald-400')
                            : (theme === 'light' ? 'bg-slate-300' : 'bg-slate-600')
                        }`} />
                        <span className="truncate">{s.title}</span>
                      </button>
                    );
                  })}
                </div>
              </aside>
            )}

            {/* Sections */}
            <div className="space-y-4 min-w-0">
              {(!page.sections || page.sections.length === 0) && (
                <div className={`rounded-2xl border border-dashed p-10 text-center ${theme === 'light' ? 'border-slate-300 text-slate-500' : 'border-white/15 text-slate-400'}`}>
                  No sections seeded for this page yet.
                </div>
              )}
              {page.sections?.map((section, idx) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  page={page}
                  onSectionChange={(updated) => handleSectionChange(idx, updated)}
                  theme={theme}
                  expanded={openId === section.id}
                  onExpandedChange={(next) => setOpenId(next ? section.id : null)}
                  anchorId={`section-${section.id}`}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {showHistory && page && (
        <PageVersions
          slug={slug}
          theme={theme}
          onClose={() => setShowHistory(false)}
          onRestored={(restored) => {
            setPage(restored);
            setOriginal(JSON.stringify(restored));
            setShowHistory(false);
          }}
        />
      )}
    </div>
  );
}
